import { useState } from "react";
import { PlusIcon } from "lucide-react";

import { EmptyDataDash } from "@/components/EmptyDataDash";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TableCell } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useUpdateSetPlacementDateSetToStart } from "@/hooks/updateSets/useUpdateSetPlacementDateSetToStart";
import { useUpdateSetPlacementStartDate } from "@/hooks/updateSets/useUpdateSetPlacementStartDate";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { dates } from "@/lib/dates";
import { earningsTracker } from "@/lib/earningsTracker";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { EarningsPlacement, PlacementStatus } from "@/types";

// Start-date column: confirmed date, a "Confirm start" flow once the planned
// date has passed, or a "+" to set a planned date on undated rows.
export const EarningsTrackerStartDateCell = ({
  placement,
}: {
  placement: EarningsPlacement;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: confirmStartDate } = useUpdateSetPlacementStartDate();
  const { mutate: setDateSetToStart } = useUpdateSetPlacementDateSetToStart();

  /* React Hooks */
  const [isConfirmPopoverOpen, setIsConfirmPopoverOpen] = useState(false);
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);

  const successToast = useSuccessToast();

  /* Helpers */
  const isAwaitingStartConfirm =
    placement.status === PlacementStatus.ACCEPTED &&
    !placement.dateStarted &&
    !!placement.dateSetToStart &&
    new Date(placement.dateSetToStart) <= new Date();

  /* Handlers */
  const handleConfirmStart = (date: Date) => {
    mixpanelUtils.trackButtonClick(
      "Earnings confirm start",
      MIXPANEL_SCREENS.EARNINGS,
    );
    setIsConfirmPopoverOpen(false);
    confirmStartDate(
      {
        companyId: placement.companyId,
        jobId: placement.jobId,
        id: placement.id,
        dateStarted: date.toISOString(),
      },
      {
        onSuccess: () => {
          successToast({
            description: `Confirmed that ${placement.candidateName} started on ${String(
              dates.formatIsoDateForUser(date.toISOString()),
            )}`,
          });
        },
      },
    );
  };

  const handleAddPlannedStart = (date: Date) => {
    mixpanelUtils.trackButtonClick(
      "Earnings add planned start date",
      MIXPANEL_SCREENS.EARNINGS,
    );
    setIsAddPopoverOpen(false);
    setDateSetToStart(
      {
        companyId: placement.companyId,
        jobId: placement.jobId,
        id: placement.id,
        dateSetToStart: date.toISOString(),
      },
      {
        onSuccess: () => {
          successToast({
            description: `${placement.candidateName} set to start on ${String(
              dates.formatIsoDateForUser(date.toISOString()),
            )}`,
          });
        },
      },
    );
  };

  /* Render */
  const render = () => {
    if (isAwaitingStartConfirm && placement.dateSetToStart) {
      const setToStartDate = new Date(placement.dateSetToStart);

      return (
        <TableCell>
          <Popover
            open={isConfirmPopoverOpen}
            onOpenChange={setIsConfirmPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 whitespace-nowrap rounded-full border-amber-200 bg-amber-50 px-2 text-xs font-medium text-amber-800 hover:bg-amber-100 hover:text-amber-800"
              >
                Confirm start
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Text className="px-3 pt-3 text-xs font-medium text-slate-500">
                Set to start{" "}
                {dates.formatIsoDateForUser(placement.dateSetToStart)}
              </Text>
              <Calendar
                mode="single"
                defaultMonth={setToStartDate}
                selected={setToStartDate}
                // Clicking the preselected day emits undefined (deselect);
                // the second arg is always the day that was clicked.
                onSelect={(date, clickedDay) => {
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  handleConfirmStart(date || clickedDay);
                }}
              />
            </PopoverContent>
          </Popover>
        </TableCell>
      );
    }

    const startDateIso = earningsTracker.getStartDateIso(placement);
    const formattedDate = dates.formatIsoDateForUser(startDateIso);

    if (formattedDate) {
      return (
        <TableCell>
          <Text className="whitespace-nowrap text-sm font-normal text-slate-700">
            {formattedDate}
          </Text>
        </TableCell>
      );
    }

    // Churned or superseded rows without a date have nothing left to schedule
    if (placement.status !== PlacementStatus.ACCEPTED) {
      return (
        <TableCell>
          <EmptyDataDash />
        </TableCell>
      );
    }

    return (
      <TableCell>
        <Popover open={isAddPopoverOpen} onOpenChange={setIsAddPopoverOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 w-8 shrink-0 rounded-full border border-slate-300 bg-white p-0 text-slate-600 shadow-sm hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
                >
                  <PlusIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>Add start date</TooltipContent>
          </Tooltip>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="single"
              onSelect={(date) => {
                if (date) handleAddPlannedStart(date);
              }}
            />
          </PopoverContent>
        </Popover>
      </TableCell>
    );
  };

  return render();
};
